import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { XCircleIcon, AlertTriangleIcon, ClockIcon, TrendingDownIcon, UsersIcon, DatabaseIcon } from 'lucide-react';
const problems = [{
  icon: ClockIcon,
  title: 'Stundenlange manuelle Dateneingabe',
  description: 'Ihr Team verbringt 40% seiner Zeit mit administrativen Aufgaben statt mit Verkaufen. Jeder Lead wird manuell erfasst, jede E-Mail einzeln geloggt.'
}, {
  icon: DatabaseIcon,
  title: 'Verstreute Kundendaten ohne Überblick',
  description: 'Informationen liegen in Excel, E-Mails, Notizen und verschiedenen Tools. Niemand hat den vollständigen Überblick über den Kunden.'
}, {
  icon: TrendingDownIcon,
  title: 'Deals fallen durch das Raster',
  description: 'Ohne systematisches Follow-up vergessen Sie potenzielle Kunden. Studien zeigen: 80% der Deals brauchen 5+ Kontakte – die meisten geben nach 2 auf.'
}, {
  icon: UsersIcon,
  title: 'Keine Transparenz im Team',
  description: 'Wer arbeitet an welchem Deal? Welche Aktivitäten wurden durchgeführt? Ohne Echtzeit-Einblick können Sie Ihr Team nicht effektiv führen.'
}, {
  icon: AlertTriangleIcon,
  title: 'Unzuverlässige Umsatzprognosen',
  description: 'Ihre Pipeline ist ein Ratespiel. Sie können nicht vorhersagen, welcher Umsatz nächsten Monat kommt – das macht Planung unmöglich.'
}, {
  icon: XCircleIcon,
  title: 'Skalierung ist unmöglich',
  description: 'Jeder neue Mitarbeiter bedeutet Chaos. Ohne standardisierte Prozesse können Sie nicht wachsen, ohne die Qualität zu verlieren.'
}];
export function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 lg:py-32 bg-[#0a0a0a]" ref={ref} data-id="element-196" data-mp="3duqesb3dsvkf">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-197" data-mp="3htz7rdqlkpex">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-198" data-mp="3hwk8u0j2n1bm">
          <span className="inline-block px-4 py-1.5 rounded-full bg-red-500/10 text-red-400 text-sm font-medium mb-6" data-id="element-199" data-mp="u1q07i1pamt0">
            Das Problem
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-200" data-mp="12z8oqwejzj9n">
            Kommt Ihnen das bekannt vor?
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-201" data-mp="1cpuxc6nur9iu">
            Die meisten Vertriebsteams kämpfen mit denselben Problemen. Das
            Ergebnis: verlorene Deals, frustrierte Mitarbeiter und stagnierender
            Umsatz.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-202" data-mp="1lscmasuw6g9j">
          {problems.map((problem, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="p-6 rounded-2xl bg-[#1a1a1a] border border-white/5" data-id="element-203" data-mp="25a8frasm2jrn" data-mpi={index}>
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4" data-id="element-204" data-mp="1co5tlwgrzw85">
                <problem.icon className="w-6 h-6 text-red-400" data-id="element-205" data-mp="1hlz3azhaf8d6" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" data-id="element-206" data-mp="13mftm28tvoyr">
                {problem.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-207" data-mp="18l0tu1o2y8n7">
                {problem.description}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.8
      }} className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 text-center" data-id="element-208" data-mp="vmjn7dlvymui">
          <p className="text-xl lg:text-2xl text-white font-medium" data-id="element-209" data-mp="7wpbizdaz4bu">
            <span className="text-red-400" data-id="element-210" data-mp="7wyy0cz52tcs">Die harte Wahrheit:</span> Jeder Tag
            ohne optimiertes CRM kostet Sie{' '}
            <span className="text-white font-bold" data-id="element-211" data-mp="22aq4j3m68x40">
              durchschnittlich 2.847€
            </span>{' '}
            an verlorenen Deals.
          </p>
        </motion.div>
      </div>
    </section>;
}